import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, CheckIcon, XIcon, ClockIcon, SearchIcon, ClipboardCheckIcon, ClipboardListIcon, ChevronLeftIcon, ChevronRightIcon, PrinterIcon, AlertTriangleIcon, UserIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Activity, Registrant } from '../../types/loisirs';

/** One roll-call outcome for a participant. */
type Mark = 'present' | 'absent' | 'retard';

/** A concrete occurrence of the activity, generated from its slots. */
interface Session {
  /** Stable key, `${slotId}-${isoDate}`. */
  id: string;
  slotId: string;
  date: string;
  startTime: string;
  endTime: string;
  /** True once the session date has passed or is today. */
  past: boolean;
}
interface Props {
  activity: Activity;
  /** Confirmed participants only — the waitlist is not called. */
  roster: Registrant[];
  onBack: () => void;
}
const WEEKDAY_LABEL: Record<number, {
  fr: string;
  en: string;
}> = {
  1: {
    fr: 'Lundi',
    en: 'Monday'
  },
  2: {
    fr: 'Mardi',
    en: 'Tuesday'
  },
  3: {
    fr: 'Mercredi',
    en: 'Wednesday'
  },
  4: {
    fr: 'Jeudi',
    en: 'Thursday'
  },
  5: {
    fr: 'Vendredi',
    en: 'Friday'
  },
  6: {
    fr: 'Samedi',
    en: 'Saturday'
  },
  7: {
    fr: 'Dimanche',
    en: 'Sunday'
  }
};

/** Stable pseudo-random value in [0,1) from a string — keeps history consistent. */
function hash(seed: string) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) % 1000 / 1000;
}

/** Attendance already recorded for past sessions, so the history reads as real. */
function seededMark(sessionId: string, registrantId: string): Mark {
  const value = hash(`${sessionId}::${registrantId}`);
  if (value > 0.88) return 'absent';
  if (value > 0.8) return 'retard';
  return 'present';
}
function isoOf(date: Date) {
  return date.toISOString().slice(0, 10);
}
export function AttendanceSheet({
  activity,
  roster,
  onBack
}: Props) {
  const {
    t,
    lang
  } = useLanguage();
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';

  /** Occurrences around today: the recent past for catching up, plus what's next. */
  const sessions: Session[] = useMemo(() => {
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const todayIso = isoOf(today);
    const out: Session[] = [];
    activity.slots.forEach(slot => {
      if (activity.recurrence === 'once') {
        const date = slot.date ?? todayIso;
        out.push({
          id: `${slot.id}-${date}`,
          slotId: slot.id,
          date,
          startTime: slot.startTime,
          endTime: slot.endTime,
          past: date <= todayIso
        });
        return;
      }
      // Walk 7 weeks back and 2 weeks forward around today.
      for (let week = -6; week <= 2; week += 1) {
        const cursor = new Date(today);
        const current = cursor.getDay() === 0 ? 7 : cursor.getDay();
        cursor.setDate(cursor.getDate() + (slot.weekday - current) + week * 7);
        const date = isoOf(cursor);
        out.push({
          id: `${slot.id}-${date}`,
          slotId: slot.id,
          date,
          startTime: slot.startTime,
          endTime: slot.endTime,
          past: date <= todayIso
        });
      }
    });
    return out.sort((a, b) => a.date === b.date ? a.startTime.localeCompare(b.startTime) : a.date.localeCompare(b.date));
  }, [activity]);

  /** Default to the most recent session that has already happened. */
  const defaultIndex = useMemo(() => {
    const lastPast = sessions.reduce((acc, session, i) => session.past ? i : acc, -1);
    return lastPast === -1 ? 0 : lastPast;
  }, [sessions]);
  const [index, setIndex] = useState(defaultIndex);
  const [marks, setMarks] = useState<Record<string, Mark>>({});
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const session = sessions[index];

  /** Marks for the open session: unsaved edits first, then the recorded history. */
  const markOf = (registrantId: string): Mark | null => {
    const key = `${session.id}::${registrantId}`;
    if (marks[key]) return marks[key];
    // The current session stays blank until the instructor calls the roll.
    if (session.past && index !== defaultIndex) {
      return seededMark(session.id, registrantId);
    }
    return null;
  };
  const setMark = (registrantId: string, mark: Mark) => {
    setMarks(prev => ({
      ...prev,
      [`${session.id}::${registrantId}`]: mark
    }));
    setSavedAt(null);
  };
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return roster.filter(person => q ? person.name.toLowerCase().includes(q) : true);
  }, [roster, search]);
  const counts = useMemo(() => {
    const tally = {
      present: 0,
      absent: 0,
      retard: 0,
      pending: 0
    };
    roster.forEach(person => {
      const mark = markOf(person.id);
      if (mark === 'present') tally.present += 1;else if (mark === 'absent') tally.absent += 1;else if (mark === 'retard') tally.retard += 1;else tally.pending += 1;
    });
    return tally;
  }, [roster, marks, session, index]);
  const presentList = roster.filter(person => {
    const mark = markOf(person.id);
    return mark === 'present' || mark === 'retard';
  });

  /** Season-to-date attendance per participant, across past sessions. */
  const rateOf = (registrantId: string) => {
    const pastSessions = sessions.filter(s => s.past && s.id !== session.id);
    if (pastSessions.length === 0) return null;
    const attended = pastSessions.filter(s => seededMark(s.id, registrantId) !== 'absent').length;
    return Math.round(attended / pastSessions.length * 100);
  };
  const longDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const shortDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short'
  });
  const markAllPresent = () => {
    const next = {
      ...marks
    };
    roster.forEach(person => {
      next[`${session.id}::${person.id}`] = 'present';
    });
    setMarks(next);
    setSavedAt(null);
  };
  const clearSheet = () => {
    const next = {
      ...marks
    };
    roster.forEach(person => {
      delete next[`${session.id}::${person.id}`];
    });
    setMarks(next);
    setSavedAt(null);
  };
  const MARK_STYLE: Record<Mark, {
    on: string;
    off: string;
    label: string;
    icon: typeof CheckIcon;
  }> = {
    present: {
      on: 'bg-emerald-600 border-emerald-600 text-white',
      off: 'bg-white border-slate-200 text-slate-500 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200',
      label: t('Présent', 'Present'),
      icon: CheckIcon
    },
    retard: {
      on: 'bg-amber-500 border-amber-500 text-white',
      off: 'bg-white border-slate-200 text-slate-500 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200',
      label: t('Retard', 'Late'),
      icon: ClockIcon
    },
    absent: {
      on: 'bg-red-600 border-red-600 text-white',
      off: 'bg-white border-slate-200 text-slate-500 hover:bg-red-50 hover:text-red-700 hover:border-red-200',
      label: t('Absent', 'Absent'),
      icon: XIcon
    }
  };
  const slotLabel = (slotId: string) => {
    const slot = activity.slots.find(s => s.id === slotId);
    if (!slot) return '';
    return `${WEEKDAY_LABEL[slot.weekday][lang]} ${slot.startTime}`;
  };
  return <div className="space-y-4 animate-in fade-in duration-200" data-id="element-2428" data-mp="3vhajb0c7l7rq">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-[#4F46E5] transition-colors" data-id="element-2429" data-mp="4ahms9sd1wtj">
        <ArrowLeftIcon className="w-4 h-4" data-id="element-2430" data-mp="wraedmi0sbex" />
        {t('Retour à l’activité', 'Back to the activity')}
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2431" data-mp="2svgq7x81cqjc">
        <div className="flex items-start justify-between gap-4 flex-wrap" data-id="element-2432" data-mp="2o3gobz8ennk4">
          <div className="min-w-0" data-id="element-2433" data-mp="2j4vkzqxuwnrg">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-indigo-50 text-[#4F46E5] text-xs font-semibold border border-indigo-100" data-id="element-2434" data-mp="2j5x8iuw272q2">
              <ClipboardCheckIcon className="w-3.5 h-3.5" data-id="element-2435" data-mp="3btkr64drrja2" />
              {t('Feuille de présences', 'Attendance sheet')}
            </span>
            <h2 className="text-xl font-bold text-[#0F172A] font-heading mt-2" data-id="element-2436" data-mp="36vrjt80qcj87">
              {activity.name[lang]}
            </h2>
            <p className="inline-flex items-center gap-1.5 text-sm text-slate-500 mt-1" data-id="element-2437" data-mp="32uz8884dy28r">
              <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-2438" data-mp="2xtib8np0uzxg" />
              {activity.venue[lang]} · {activity.ageRange} {t('ans', 'yrs')}
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0" data-id="element-2439" data-mp="1mdl73s8m49fn">
            <button onClick={clearSheet} className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-2440" data-mp="1wu7q07zfckh">
              <XIcon className="w-4 h-4" data-id="element-2441" data-mp="avpfvhul6wqf" />
              {t('Réinitialiser', 'Reset')}
            </button>
            <button onClick={markAllPresent} className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-2442" data-mp="fpa2kdqqc4ab">
              <CheckIcon className="w-4 h-4" data-id="element-2443" data-mp="ukzsgmz1d1kb" />
              {t('Tout marquer présent', 'Mark all present')}
            </button>
            <button onClick={() => setSavedAt(new Date().toLocaleTimeString(locale, {
            hour: '2-digit',
            minute: '2-digit'
          }))} disabled={counts.pending === roster.length} className="inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-2444" data-mp="ymbao7fkykih">
              <ClipboardCheckIcon className="w-4 h-4" data-id="element-2445" data-mp="4pd400lyvuyh" />
              {t('Enregistrer l’appel', 'Save roll call')}
            </button>
          </div>
        </div>

        {/* Session navigation */}
        <div className="flex items-center justify-between gap-3 mt-5 pt-5 border-t border-slate-100 flex-wrap" data-id="element-2446" data-mp="dp05emep50rj">
          <div className="flex items-center gap-2" data-id="element-2447" data-mp="ifyp7vyw8gzj">
            <button onClick={() => setIndex(Math.max(index - 1, 0))} disabled={index === 0} aria-label={t('Séance précédente', 'Previous session')} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors" data-id="element-2448" data-mp="iikv7r64yofn">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-2449" data-mp="2hvtgv5ghukc5" />
            </button>
            <div className="min-w-0" data-id="element-2450" data-mp="2cwqddpg5wm6a">
              <p className="text-sm font-semibold text-[#0F172A] capitalize" data-id="element-2451" data-mp="2cvnnnkukspry">
                {longDate(session.date)}
              </p>
              <p className="text-xs text-slate-500" data-id="element-2452" data-mp="27z70asv7affx">
                {session.startTime} – {session.endTime}
                {activity.slots.length > 1 && ` · ${slotLabel(session.slotId)}`}
                {!session.past && ` · ${t('à venir', 'upcoming')}`}
              </p>
            </div>
            <button onClick={() => setIndex(Math.min(index + 1, sessions.length - 1))} disabled={index === sessions.length - 1} aria-label={t('Séance suivante', 'Next session')} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors" data-id="element-2453" data-mp="2vp1af2o4xpwh">
              <ChevronRightIcon className="w-4 h-4" data-id="element-2454" data-mp="3ek9bhfygnjw4" />
            </button>
            {index !== defaultIndex && <button onClick={() => setIndex(defaultIndex)} className="px-2.5 py-1 rounded-lg text-xs font-semibold text-[#4F46E5] hover:bg-indigo-50 transition-colors" data-id="element-2455" data-mp="1cqd588z5p5dw">
                {t('Séance du jour', 'Today’s session')}
              </button>}
          </div>

          {/* Live tally */}
          <div className="flex items-center gap-2 flex-wrap" data-id="element-2456" data-mp="txix9yzyadj6">
            {[{
            value: counts.present,
            label: t('présents', 'present'),
            className: 'bg-emerald-50 text-emerald-700 border-emerald-200'
          }, {
            value: counts.retard,
            label: t('retards', 'late'),
            className: 'bg-amber-50 text-amber-700 border-amber-200'
          }, {
            value: counts.absent,
            label: t('absents', 'absent'),
            className: 'bg-red-50 text-red-700 border-red-200'
          }, {
            value: counts.pending,
            label: t('non pointés', 'not called'),
            className: 'bg-slate-50 text-slate-600 border-slate-200'
          }].map(chip => <span key={chip.label} className={`inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${chip.className}`} data-id="element-2457" data-mp="qfuz7mobyl27">
                <span className="text-sm font-bold tabular-nums" data-id="element-2458" data-mp="10e0xluaxh92b">
                  {chip.value}
                </span>
                {chip.label}
              </span>)}
          </div>
        </div>

        {savedAt && <p className="flex items-center gap-2 mt-4 rounded-lg border border-emerald-200 bg-emerald-50/70 px-4 py-2.5 text-sm text-emerald-800" data-id="element-2459" data-mp="326ffitdh2diq">
            <CheckIcon className="w-4 h-4 flex-shrink-0" data-id="element-2460" data-mp="2x8no00gqotf2" />
            {t(`Appel enregistré à ${savedAt}. Les absences répétées sont signalées au responsable du programme.`, `Roll call saved at ${savedAt}. Repeated absences are flagged to the program lead.`)}
          </p>}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4 items-start" data-id="element-2461" data-mp="229nfxarlozyk">
        {/* Roll call */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-2462" data-mp="15jxrin9tdedr">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap" data-id="element-2463" data-mp="1ahfzaac3hnk6">
            <div data-id="element-2464" data-mp="1eii374gyinsh">
              <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-2465" data-mp="1ei013r7m5fcn">
                {t('Appel', 'Roll call')}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-2466" data-mp="1jc4a0g0fvxgd">
                {roster.length}{' '}
                {t('participants inscrits', 'registered participants')}
              </p>
            </div>
            <div className="relative" data-id="element-2467" data-mp="jah7v1lgf2z3">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-2468" data-mp="j9ehqvvyh4k8" />
              <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('Rechercher un nom…', 'Search a name…')} aria-label={t('Rechercher un participant', 'Search a participant')} className="w-56 pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-2469" data-mp="ebwad9xl6xec" />
            </div>
          </div>

          {visible.length === 0 ? <div className="py-16 text-center" data-id="element-2470" data-mp="3iq4gca2uw6od">
              <div className="mx-auto w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3" data-id="element-2471" data-mp="3irfiy6kkf8dw">
                <UserIcon className="w-6 h-6 text-slate-400" data-id="element-2472" data-mp="3dskog7d8jvdy" />
              </div>
              <p className="text-sm font-semibold text-slate-700" data-id="element-2473" data-mp="38rwy7nwtziks">
                {roster.length === 0 ? t('Aucun participant confirmé', 'No confirmed participant') : t('Aucun nom ne correspond', 'No matching name')}
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-2474" data-mp="2p6ognwr3wdrb">
                {roster.length === 0 ? t('Les inscriptions confirmées apparaîtront ici pour l’appel.', 'Confirmed registrations will appear here for the roll call.') : t('Ajustez votre recherche.', 'Adjust your search.')}
              </p>
            </div> : <ul className="divide-y divide-slate-100" data-id="element-2475" data-mp="3847iafb8iu3p">
              {visible.map(person => {
            const mark = markOf(person.id);
            const rate = rateOf(person.id);
            return <li key={person.id} className={`px-5 py-3.5 transition-colors ${mark === 'absent' ? 'bg-red-50/40' : mark ? 'bg-emerald-50/30' : 'hover:bg-slate-50/60'}`} data-id="element-2476" data-mp="4lxnflfxkxom">
                    <div className="flex items-center gap-4 flex-wrap" data-id="element-2477" data-mp="23tw6qbxvfgcg">
                      <div className="min-w-0 flex-1" data-id="element-2478" data-mp="1ywxgi4hbvtjj">
                        <p className="text-sm font-medium text-slate-900" data-id="element-2479" data-mp="1b60h1r6pyop6">
                          {person.name}
                          <span className="ml-2 text-xs font-normal text-slate-400" data-id="element-2480" data-mp="167gq1cd9qmpy">
                            {person.age} {t('ans', 'yrs')}
                          </span>
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5" data-id="element-2481" data-mp="1g4ldrgmriq8v">
                          {person.guardian && <span className="inline-flex items-center gap-1" data-id="element-2482" data-mp="2vkk91rba6rrp">
                              <PhoneIcon className="w-3 h-3 text-slate-400" data-id="element-2483" data-mp="2rl1nqufjseoc" />
                              {person.guardian}
                            </span>}
                          {rate !== null && <span className={`${person.guardian ? 'ml-2' : ''} ${rate < 70 ? 'text-amber-700 font-medium' : ''}`} data-id="element-2484" data-mp="tx7iybntk1xc">
                              {t('Assiduité ', 'Attendance ')}
                              {rate} %
                            </span>}
                        </p>
                        {person.missing && <p className="inline-flex items-start gap-1.5 text-xs text-amber-700 mt-1" data-id="element-2485" data-mp="1hmtjp3juap7g">
                            <AlertTriangleIcon className="w-3.5 h-3.5 mt-px flex-shrink-0" data-id="element-2486" data-mp="1mkv3jqp3asjz" />
                            {person.missing[lang]}
                          </p>}
                      </div>

                      <div role="group" aria-label={t(`Présence de ${person.name}`, `Attendance for ${person.name}`)} className="flex items-center gap-1.5 shrink-0" data-id="element-2487" data-mp="3oekxfcv8gb2l">
                        {(['present', 'retard', 'absent'] as Mark[]).map(option => {
                    const style = MARK_STYLE[option];
                    const Icon = style.icon;
                    const on = mark === option;
                    return <button key={option} onClick={() => setMark(person.id, option)} aria-pressed={on} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-1 ${on ? style.on : style.off}`} data-id="element-2488" data-mp="2fei6ejaebnfe">
                                <Icon className="w-3.5 h-3.5" data-id="element-2489" data-mp="10ylp81byn7cb" />
                                {style.label}
                              </button>;
                  })}
                      </div>
                    </div>
                  </li>;
          })}
            </ul>}

          <div className="px-5 py-4 border-t border-slate-100" data-id="element-2490" data-mp="1omd2p898lqf6">
            <label htmlFor="attendance-note" className="block text-xs font-medium text-slate-600 mb-1" data-id="element-2491" data-mp="1jobmqw73y77x">
              {t('Note de séance', 'Session note')}
            </label>
            <textarea id="attendance-note" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('Incident, blessure, remplacement d’instructeur…', 'Incident, injury, instructor replacement…')} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-2492" data-mp="8br7hc6j2m2j" />
          </div>
        </div>

        {/* Present list + history */}
        <div className="space-y-4" data-id="element-2493" data-mp="2yle8tmsylh7v">
          <section className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-2494" data-mp="2yoigwv9jox3t">
            <div className="flex items-center justify-between gap-2 mb-3" data-id="element-2495" data-mp="32mg9m82iy1by">
              <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-2496" data-mp="37o5l1otnsh0k">
                {t('Liste des présents', 'Present list')}
              </h3>
              <button aria-label={t('Imprimer la liste', 'Print the list')} title={t('Imprimer la liste', 'Print the list')} className="p-2 rounded-lg text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 transition-colors" data-id="element-2497" data-mp="3cjulrvhf03yg">
                <PrinterIcon className="w-4 h-4" data-id="element-2498" data-mp="1a9kozx9480gc" />
              </button>
            </div>

            {presentList.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center" data-id="element-2499" data-mp="1k6774ea24deo">
                {t('Personne n’a encore été pointé pour cette séance.', 'Nobody has been called for this session yet.')}
              </p> : <>
                <p className="text-xs text-slate-500 mb-2.5" data-id="element-2500" data-mp="1p5adq35oqror">
                  {presentList.length} / {roster.length}{' '}
                  {t('participants sur place', 'participants on site')}
                </p>
                <ol className="space-y-1.5" data-id="element-2501" data-mp="1xy3w49gfi76u">
                  {presentList.map((person, i) => <li key={person.id} className="flex items-center gap-2.5 text-sm text-slate-700" data-id="element-2502" data-mp="2cvegy9eyopf8" data-mpi={i}>
                      <span className="w-5 text-xs text-slate-400 tabular-nums" data-id="element-2503" data-mp="2rhbi4so8eqrn">
                        {i + 1}.
                      </span>
                      <span className="min-w-0 flex-1 truncate" data-id="element-2504" data-mp="2vhc9fdwfiwg4">
                        {person.name}
                      </span>
                      {markOf(person.id) === 'retard' && <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-semibold border border-amber-200" data-id="element-2505" data-mp="2mgw3lxs7rd83">
                          {t('retard', 'late')}
                        </span>}
                    </li>)}
                </ol>
              </>}
          </section>

          <section className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-2506" data-mp="23rh8sl34fkc5">
            <h3 className="text-base font-semibold text-[#0F172A] mb-1" data-id="element-2507" data-mp="23pd1ru9xe57n">
              {t('Séances précédentes', 'Previous sessions')}
            </h3>
            <p className="text-xs text-slate-500 mb-4" data-id="element-2508" data-mp="1ts8flug2y9qu">
              {t('Taux de présence par séance', 'Attendance rate per session')}
            </p>
            <ul className="space-y-2.5" data-id="element-2509" data-mp="2x60bbl4wpwg0">
              {sessions.filter(s => s.past).slice(-6).reverse().map(item => {
              const present = item.id === session.id ? presentList.length : roster.filter(person => seededMark(item.id, person.id) !== 'absent').length;
              const rate = roster.length ? Math.round(present / roster.length * 100) : 0;
              const current = item.id === session.id;
              return <li key={item.id} data-id="element-2510" data-mp="2nwtyd5ax003">
                      <button onClick={() => setIndex(sessions.findIndex(s => s.id === item.id))} className="w-full text-left group" data-id="element-2511" data-mp="2mu7qiixblqi">
                        <div className="flex items-baseline justify-between gap-2 mb-1" data-id="element-2512" data-mp="a7ejc7588k4k">
                          <span className={`text-xs ${current ? 'font-bold text-[#4F46E5]' : 'text-slate-600 group-hover:text-slate-900'}`} data-id="element-2513" data-mp="eyecr2f50k2b">
                            {shortDate(item.date)} · {item.startTime}
                          </span>
                          <span className="text-xs font-bold text-slate-700 tabular-nums" data-id="element-2514" data-mp="3sd1a9jnys4oa">
                            {present}/{roster.length}
                          </span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-2515" data-mp="17nv6c3ehu704">
                          <div className={`h-full ${rate >= 85 ? 'bg-emerald-500' : rate >= 70 ? 'bg-amber-400' : 'bg-red-500'}`} style={{
                      width: `${rate}%`
                    }} data-id="element-2516" data-mp="1cldfnuwfao8n" />
                        </div>
                      </button>
                    </li>;
            })}
            </ul>
          </section>

          <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4" data-id="element-2517" data-mp="2l08p6jpfx0wt">
            <p className="flex items-start gap-2 text-sm text-[#3730A3]" data-id="element-2518" data-mp="2yr3ph7lvi9os">
              <ClipboardListIcon className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-2519" data-mp="2yt81ydxu8h0o" />
              {t('L’instructeur peut faire l’appel depuis une tablette au bord du plateau. Trois absences consécutives déclenchent un avis au parent.', 'The instructor can call the roll from a tablet at the venue. Three consecutive absences trigger a notice to the parent.')}
            </p>
          </div>
        </div>
      </div>
    </div>;
}